import { useContext, useState } from 'react';
import { batch, useDispatch, useSelector } from 'react-redux';

import { contentOptimizerPathes } from '../../../../../constants/queryPathes';
import { statusCodes } from '../../../../../constants/statusCodes';
import { editorTypes } from '../../constants/tiptapEditorConstants';

import { ContentOptimizerContext } from '../../../../hocs/ContentOptimizerProvider';

import { axiosAPI } from '../../../../../utils/axiosAPI';
import { transformContentData } from '../../../../../utils/helpers/editor/slateToHtml';
import { getAxiosHeaders } from '../../../../../utils/helpers/getAxiosHeaders';
import { openBasicErrorNotification } from '../../../../../utils/helpers/openBasicErrorNotification';
import { useOrganizationSlug } from '../../../../../utils/hooks/useOrganizationSlug';
import {
    getVersionBasicData,
    parseEditorContent,
    refactorEditorVersion,
    sortVersionsByDescendingDate,
} from '../../utils/versionHelpers';

import {
    addEditorVersion,
    deleteEditorVersion,
    setEditorPreviewVersion,
    updatePreviewVersionContentLoading,
    updateCurrentEditorContent,
    updateCurrentEditorLoading,
    updateCurrentEditorVersion,
    updateEditorBasicData,
    updateEditorVersionName,
    updateEditorVersions,
} from '../../store/tiptapEditor.actions';
import {
    selectCurrentEditorContent,
    selectCurrentEditorText,
    selectEditorPreviewVersion,
    selectPreviewVersionContentLoading,
} from '../../store/tiptapEditor.selectors';

const editorType = editorTypes.OPTIMIZER;

const useOptimizerEditorVersions = () => {
    const dispatch = useDispatch();

    const contentOptimizerId = useContext(ContentOptimizerContext);

    const organizationSlug = useOrganizationSlug();

    const currentEditorContent = useSelector(selectCurrentEditorContent(editorType));
    const currentEditorText = useSelector(selectCurrentEditorText(editorType));
    const editorPreviewVersion = useSelector(selectEditorPreviewVersion(editorType));
    const previewVersionLoading = useSelector(selectPreviewVersionContentLoading(editorType));

    const [isVersionCreating, setIsVersionCreating] = useState(false);
    const [isVersionUpdating, setIsVersionUpdating] = useState(false);

    const createNewEditorVersion = (additionalData = '') => {
        if (contentOptimizerId) {
            setIsVersionCreating(true);

            const isAiGenerated = !!additionalData?.length;
            const additionalFields = isAiGenerated
                ? {
                      active: true,
                      is_ai_generated: isAiGenerated,
                  }
                : {};
            const currentContent = isAiGenerated ? '' : currentEditorContent || '';

            return axiosAPI
                .post(
                    `${contentOptimizerPathes.getContentOptimizers}/${contentOptimizerId}/versions`,
                    {
                        content: JSON.stringify(currentContent + additionalData),
                        content_text: isAiGenerated ? '' : currentEditorText || '',
                        ...additionalFields,
                    },
                    {
                        ...getAxiosHeaders(),
                        params: { slug: organizationSlug },
                    }
                )
                .then((res) => {
                    const resultData = res?.data;

                    if (isAiGenerated) {
                        dispatch(updateEditorBasicData(resultData));
                    } else {
                        const refactoredVersion = refactorEditorVersion(resultData, editorType);

                        dispatch(addEditorVersion({ editorType, version: refactoredVersion }));
                    }

                    return resultData;
                })
                .catch(() => {
                    openBasicErrorNotification();
                })
                .finally(() => {
                    setIsVersionCreating(false);
                });
        }
    };

    const fetchEditorVersions = () => {
        if (contentOptimizerId) {
            dispatch(updateCurrentEditorLoading({ editorType, loading: true }));

            axiosAPI
                .get(
                    `${contentOptimizerPathes.getContentOptimizers}/${contentOptimizerId}/versions`,
                    {
                        ...getAxiosHeaders(),
                        params: { slug: organizationSlug },
                    }
                )
                .then((result) => {
                    if (result?.status === statusCodes.success) {
                        if (result.data?.length > 0) {
                            let currentVersion = {};

                            const transformedData = result.data.map((dataItem) => ({
                                ...dataItem,
                                content: transformContentData(dataItem.content, dataItem.active), // JSON.parse -> slateToHtml
                            }));
                            const sortedVersions = sortVersionsByDescendingDate(transformedData);
                            const refactoredVersions = sortedVersions.map((version) => {
                                if (version.active) {
                                    currentVersion = {
                                        ...version,
                                        content: parseEditorContent(version.content, editorType),
                                    };
                                }

                                return refactorEditorVersion(version, editorType);
                            });

                            batch(() => {
                                dispatch(
                                    updateEditorVersions({
                                        editorType,
                                        versions: refactoredVersions,
                                    })
                                );

                                if (currentVersion) {
                                    dispatch(
                                        updateCurrentEditorVersion({
                                            editorType,
                                            version: currentVersion,
                                        })
                                    );
                                    dispatch(
                                        updateCurrentEditorContent({
                                            editorType,
                                            content: currentVersion.content,
                                        })
                                    );
                                    dispatch(
                                        updateEditorBasicData(getVersionBasicData(currentVersion))
                                    );
                                }
                            });
                        } else {
                            createNewEditorVersion();
                        }
                    }
                })
                .catch(() => {
                    openBasicErrorNotification();
                })
                .finally(() => {
                    dispatch(updateCurrentEditorLoading({ editorType, loading: false }));
                    setIsVersionCreating(false);
                });
        }
    };

    const handleActivateEditorVersion = (editorId) => {
        if (contentOptimizerId && editorId) {
            dispatch(updateCurrentEditorLoading({ editorType, loading: true }));

            axiosAPI
                .patch(
                    `${contentOptimizerPathes.getContentOptimizers}/${contentOptimizerId}/versions/${editorId}`,
                    {
                        active: true,
                    },
                    {
                        ...getAxiosHeaders(),
                        params: { slug: organizationSlug },
                    }
                )
                .then((result) => {
                    const currentVersion = result?.data;

                    if (currentVersion) {
                        fetchEditorVersions();
                    }
                })
                .catch((error) => {
                    console.error(error);
                    openBasicErrorNotification();
                });
        }
    };

    const handlePreviewEditorVersion = (versionId) => {
        if (!editorPreviewVersion && !previewVersionLoading) {
            dispatch(updatePreviewVersionContentLoading({ editorType, loading: true }));

            axiosAPI
                .get(
                    `${contentOptimizerPathes.getContentOptimizers}/${contentOptimizerId}/versions/${versionId}`,
                    {
                        ...getAxiosHeaders(),
                    }
                )
                .then((result) => {
                    const previewedVersion = result.data;
                    const trimmedCharPosition = 1;

                    dispatch(
                        setEditorPreviewVersion({
                            editorType,
                            versionId: versionId && !editorPreviewVersion ? versionId : null,
                            updateBasicData: true,
                            versionContent: previewedVersion.content.slice(
                                trimmedCharPosition,
                                -trimmedCharPosition
                            ),
                        })
                    );
                })
                .catch(() => {
                    openBasicErrorNotification();
                })
                .finally(() => {
                    dispatch(updatePreviewVersionContentLoading({ editorType, loading: false }));
                });
        } else {
            dispatch(
                setEditorPreviewVersion({
                    editorType,
                    versionId: versionId && !editorPreviewVersion ? versionId : null,
                    updateBasicData: true,
                })
            );
        }
    };

    const handleUpdateVersionName = (versionId, versionName) => {
        if (contentOptimizerId && versionId) {
            axiosAPI
                .patch(
                    `${contentOptimizerPathes.getContentOptimizers}/${contentOptimizerId}/versions/${versionId}`,
                    {
                        name: versionName,
                    },
                    {
                        ...getAxiosHeaders(),
                        params: { slug: organizationSlug },
                    }
                )
                .then(() => {
                    dispatch(
                        updateEditorVersionName({ editorType, id: versionId, name: versionName })
                    );
                })
                .catch(() => {
                    openBasicErrorNotification();
                });
        }
    };

    const handleDeleteEditorVersion = (versionId) => {
        if (contentOptimizerId && versionId) {
            axiosAPI
                .delete(
                    `${contentOptimizerPathes.deleteContentOptimizer}/${contentOptimizerId}/versions/${versionId}`,
                    {
                        ...getAxiosHeaders(),
                        params: { slug: organizationSlug },
                    }
                )
                .then(() => {
                    dispatch(deleteEditorVersion({ editorType, id: versionId }));
                })
                .catch(() => {
                    openBasicErrorNotification();
                });
        }
    };

    const updateEditorVersionStatus = (versionId) => {
        if (isVersionUpdating) return;

        if (contentOptimizerId && versionId) {
            setIsVersionUpdating(true);

            const formattedRequestPath = contentOptimizerPathes.updateContentStatus.replace(
                '{version_id}',
                versionId
            );

            axiosAPI
                .patch(
                    formattedRequestPath,
                    {},
                    {
                        ...getAxiosHeaders(),
                        params: { slug: organizationSlug },
                    }
                )
                .then((result) => {
                    const currentVersion = result?.data;

                    if (currentVersion) {
                        dispatch(
                            updateCurrentEditorVersion({
                                editorType,
                                version: currentVersion,
                            })
                        );
                    }
                })
                .catch(() => {
                    openBasicErrorNotification();
                })
                .finally(() => setIsVersionUpdating(false));
        } else {
            openBasicErrorNotification('Incorrect optimizer data');
        }
    };

    return {
        fetchEditorVersions,
        createNewEditorVersion,
        handleActivateEditorVersion,
        handlePreviewEditorVersion,
        handleUpdateVersionName,
        handleDeleteEditorVersion,
        isVersionCreating,
        setIsVersionCreating,
        updateEditorVersionStatus,
    };
};

export default useOptimizerEditorVersions;
